/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {useCurrentCustomer} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-customer'
import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'

const CheckoutContext = React.createContext()

export const CheckoutProvider = ({children}) => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const [step, setStep] = useState()
    const initialHasTriggered = useRef(false)

    const CHECKOUT_STEPS_LIST = [
        'CONTACT_INFO',
        'SHIPPING_ADDRESS',
        'PAYMENT',
    ]
    const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, step, idx) => ({...acc, [step]: idx}), {})

    useEffect(() => {
        if (!customer || !basket || initialHasTriggered.current) {
            return
        }

        initialHasTriggered.current = true

        let step = STEPS.PAYMENT

        const basketShipping = basket?.shipments[0]?.shippingAddress
        const isBasketShippingValid = basketShipping && Object.keys(basketShipping).length > 1

        if (customer.isGuest && !basket.customerInfo?.email) {
            step = STEPS.CONTACT_INFO
        } else if (!isBasketShippingValid || !basket.shipments[0]?.shippingMethod) {
            step = STEPS.SHIPPING_ADDRESS
        }

        setStep(step)
    }, [
        customer,
        basket
    ])

    const value = {
        step,
        STEPS,
        goToNextStep: () => setStep(step + 1),
        goToStep: (step) => setStep(step)
    }

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
    children: PropTypes.any
}

/**
 * A hook for managing checkout state and actions
 * @returns {Object} Checkout data and actions
 */
export const useCheckout = () => {
    return React.useContext(CheckoutContext)
}
